import React, { useMemo } from "react";
import type { RecoveryDailyLog, SymptomKey } from "../types/recovery";
import type { SymptomDaySummary } from "../utils/symptomUtils";
import { getSymptomRisk, riskToBgColor } from "../utils/symptomUtils";
import { getBleedingBgColor } from "../utils/bleedingUtils";

type SymptomWeekRowProps = {
  title: string;
  days: SymptomDaySummary[];
  mode: "symptom" | "bleeding";
  symptomKey?: SymptomKey;
  onDayClick?: (day: SymptomDaySummary) => void;
  onEmptyDayClick?: (day: SymptomDaySummary, index: number, element: HTMLElement) => void;
};

const getDayBgColor = (
  log: RecoveryDailyLog | undefined,
  mode: "symptom" | "bleeding",
  symptomKey?: SymptomKey
): string => {
  if (mode === "bleeding") {
    if (log?.symptoms?.vaginalBleeding) {
      // If there is a color logged, show that physical bleeding color. 
      // If no color is logged (meaning "No Bleeding"), display the healthy green.
      return log.symptoms.vaginalBleeding.colour
        ? getBleedingBgColor(log.symptoms.vaginalBleeding.colour)
        : "#D1E0B7"; 
    }
    return "#C0C0C0"; // Default unlogged gray
  }
  if (!symptomKey) return "#C0C0C0";
  if (symptomKey === "vaginalBleeding") {
    const risk = log?.symptoms?.vaginalBleeding?.overallRisk?.toLowerCase().trim();
    switch (risk) {
      case "alert": return "#FCB3AD";
      case "monitor": return "#FFE0C0";
      case "ok":
      case "normal": return "#D1E0B7";
      default: return "#C0C0C0";
    }
  }
  const risk = getSymptomRisk(log ?? null, symptomKey);
  return riskToBgColor(risk);
};

const hasLogData = (day: SymptomDaySummary, mode: "symptom" | "bleeding", symptomKey?: SymptomKey): boolean => {
  if (!day.log) return false;
  if (mode === "bleeding") {
    // Validates completion based on whether the bleeding object itself exists, regardless of selected colour
    return !!day.log.symptoms?.vaginalBleeding;
  }
  if (!symptomKey) return false;
  const symptomRecord = day.log.symptoms as Partial<Record<SymptomKey, { risk?: string }>>;
  return !!symptomRecord?.[symptomKey]?.risk;
};

export const SymptomWeekRow: React.FC<SymptomWeekRowProps> = ({
  title,
  days,
  mode,
  symptomKey,
  onDayClick,
  onEmptyDayClick,
}) => {
  const monthDisplay = useMemo(() => {
    return new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" });
  }, []);

  const last7 = useMemo(() => days.slice(-7), [days]);

  return (
    <div className="item">
      <div className="label">
        <div className="content">
          <div className="title">{title}</div>
          <div className="week-block">
            <div className="month">{monthDisplay}</div>
            <div className="week">
              {last7.map((day, index) => {
                const bgColor = getDayBgColor(day.log, mode, symptomKey);
                const dayNumber = day.dateId.split("-")[2] || "";
                const hasData = hasLogData(day, mode, symptomKey);
                const isInteractive = !!(onDayClick || onEmptyDayClick);

                const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
                  if (hasData && onDayClick) {
                    onDayClick(day);
                  } else if (!hasData && onEmptyDayClick) {
                    onEmptyDayClick(day, index, e.currentTarget);
                  }
                };

                return (
                  <div
                    key={day.dateId}
                    className={`day ${isInteractive ? "cursor-pointer hover:scale-105 transition-transform" : ""}`}
                    style={{ backgroundColor: bgColor }}
                    onClick={isInteractive ? handleClick : undefined}
                  >
                    {dayNumber}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};